//@ts-ignore
import html2pdf from 'html2pdf.js'

import EditorTheme from '@/components/edit/editor-theme'
import { CommentNode } from '@/components/edit/nodes/CommentNode'
import { ReviewTextNode } from '@/components/edit/review/ReviewTextNode'
import { eventsService } from '@/services/analytics/events-service'
import { EventCategory, EventName } from '@interfaces/events'
import { ExtendedSerializedEditorState } from '@interfaces/lexical'
import { createHeadlessEditor } from '@lexical/headless'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { LinkNode } from '@lexical/link'
import { MarkNode } from '@lexical/mark'
import { ai as aiService } from '@/services/ai/ai-service'
import { docs as docsService } from '@/services/docs/docs-service'
import { AlignmentType, Document, HeadingLevel, Packer, Paragraph, TextRun } from 'docx'
import { saveAs } from 'file-saver'
import {
  $createRangeSelection,
  $getRoot,
  $insertNodes,
  $isElementNode,
  $setSelection,
  LexicalEditor,
  LexicalNode,
} from 'lexical'
import { $isSuggestionNode, SuggestionData, SuggestionNode } from '../nodes/SuggestionNode'
import { CustomHeadingNode } from '../review/CustomHeadingNode'
import { CustomListItemNode } from '../review/CustomListItemNode'
import { CustomListNode } from '../review/CustomListNode'
import { CustomParagraphNode } from '../review/CustomParagraphNode'
import { EngineAiOutput, StreamingTextActionDto } from '@interfaces/ai'
import { CREATE_SUGGESTION_COMMAND } from '../plugins/SuggestionPlugin'
import { Subscription } from '@/pages/app/pricing'
import { BillingModalCopyKey } from '@/contexts/billing'
import { SubscriptionStatus } from '@interfaces/auth'
import { toast } from 'react-hot-toast'

export const getEditorNodes = () => {
  return [
    CustomHeadingNode,
    CustomListNode,
    CustomListItemNode,
    CustomParagraphNode,

    MarkNode,
    LinkNode,
    CommentNode,
    SuggestionNode,
    ReviewTextNode, // Always include to support existing review nodes
  ]
}

/**
 * Cleans HTML content by removing or replacing unwanted HTML entities and special characters
 */
function cleanHtmlContent(htmlString: string): string {
  return (
    htmlString
      // Replace non-breaking spaces with regular spaces
      .replace(/&nbsp;/g, ' ')
      // Replace other common HTML entities that can cause rendering issues
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      // Remove zero-width characters and other invisible Unicode characters
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      // Clean up multiple consecutive spaces (but keep intentional formatting)
      .replace(/  +/g, ' ')
      // Remove any remaining unrecognized HTML entities (&#...;)
      .replace(/&#\d+;/g, '')
      .replace(/&#x[0-9a-fA-F]+;/g, '')
  )
}

/**
 * Recursively cleans malformed link nodes from editor state JSON
 * Converts link nodes with empty/invalid URLs to text nodes
 */
function cleanMalformedLinkNodes(
  nodes: ExtendedSerializedEditorState['root']['children']
): ExtendedSerializedEditorState['root']['children'] {
  const processedNodes: any[] = []

  for (const node of nodes) {
    // Check if this is a malformed link node by casting to any to access link properties
    const linkNode = node as any
    if (linkNode.type === 'link' && (!linkNode.url || linkNode.url.trim() === '')) {
      // Convert malformed link to its text content
      if (linkNode.children && linkNode.children.length > 0) {
        // If the link has children, flatten them into text nodes
        for (const child of linkNode.children) {
          if (child.type === 'text') {
            // Create a clean text node without link properties
            const cleanTextNode = {
              ...child,
              type: 'text',
            }
            // Remove any potential link properties
            delete (cleanTextNode as any).rel
            delete (cleanTextNode as any).url
            delete (cleanTextNode as any).title
            delete (cleanTextNode as any).target
            processedNodes.push(cleanTextNode)
          } else if (child.children) {
            // Recursively clean nested children
            processedNodes.push({
              ...child,
              children: cleanMalformedLinkNodes(child.children),
            })
          } else {
            processedNodes.push(child)
          }
        }
      }
      // If link has no children, skip it (effectively removing it)
    } else {
      // For non-malformed nodes, recursively clean children if they exist
      if (node.children && Array.isArray(node.children)) {
        const cleanedChildren = cleanMalformedLinkNodes(node.children)
        processedNodes.push({
          ...node,
          children: cleanedChildren,
        })
      } else {
        processedNodes.push(node)
      }
    }
  }

  return processedNodes
}

/**
 * Cleans an entire editor state by removing malformed link nodes.
 * These can appear when opening a document in Lexical directly from
 * CloudConvert without stripping out special characters.
 */
export function cleanEditorState(editorState: ExtendedSerializedEditorState): ExtendedSerializedEditorState {
  return {
    ...editorState,
    root: {
      ...editorState.root,
      children: cleanMalformedLinkNodes(editorState.root.children),
    },
  }
}

export function parseHtmlToLexicalJsonState(htmlString: string): ExtendedSerializedEditorState {
  const headlessEditor = createHeadlessEditor({
    namespace: 'ParseHtmlToLexical',
    theme: EditorTheme,
    nodes: getEditorNodes(),
  })

  // Clean the HTML content before parsing
  const cleanedHtmlString = cleanHtmlContent(htmlString)
  const dom = new DOMParser().parseFromString(cleanedHtmlString, 'text/html')

  headlessEditor.update(
    () => {
      const nodes = $generateNodesFromDOM(headlessEditor, dom)
      const root = $getRoot()
      root.clear()
      $insertNodes(nodes)
    },
    { discrete: true }
  )

  const editorState = headlessEditor.getEditorState().toJSON() as ExtendedSerializedEditorState
  assignBlockIdsToEditorState(editorState)

  return editorState
}

export const getHtmlFromEditor = (editor: any) => {
  return editor.getEditorState().read(() => $generateHtmlFromNodes(editor, null))
}

export function generateRandomId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 10)
    : Math.random().toString(36).substring(2, 12)
}

export function assignBlockIdsToEditorState(editorState: ExtendedSerializedEditorState) {
  // helper function
  const assignRecursively = (child: ExtendedSerializedEditorState['root']['children'][number], index: number) => {
    if (child.type === 'list' && child.children && child.children.length > 0) {
      child.children.forEach((listItem, listIndex) => {
        assignRecursively(listItem, listIndex)
      })
    }

    // handle nested list items
    if (child.type === 'listitem' && child.children && child.children.length > 0) {
      const firstChild = child.children[0]

      if (firstChild.type === 'list' && firstChild.children && firstChild.children.length > 0) {
        firstChild.children.forEach((listItem, listIndex) => {
          assignRecursively(listItem, listIndex)
        })
      }
    }

    if (child.__blockId) return
    child.__blockId = `${index}${generateRandomId()}`.substring(0, 10)
  }

  // actual function declaration
  const children = editorState.root.children

  children.forEach((child, index) => {
    assignRecursively(child, index)
  })
}

/** Preprocess EditorJS blocks to detect and convert heading-like content */
export function preprocessEditorJSBlocks(blocks: any[]): any[] {
  return blocks.map(block => {
    if (block.type === 'paragraph' && block.data?.text) {
      const text = block.data.text

      const plainText = text.replace(/<[^>]*>/g, '').trim()

      // Check if this looks like a heading
      const isPossibleHeading =
        plainText.length < 100 && // Short text (likely a title)
        (text.includes('<b>') || text.includes('<strong>')) && // Contains bold formatting
        (plainText === plainText.toUpperCase() ||
          plainText.split(' ').every((word: string) => word.length > 0 && word[0] === word[0].toUpperCase())) && // Is all caps or title case
        plainText.length > 5 // Not too short (avoid single words)

      if (isPossibleHeading) {
        // Determine heading level based on text characteristics
        let level = 1

        // Very short, all caps = H1
        if (plainText.length < 30 && plainText === plainText.toUpperCase()) {
          level = 1
        }
        // Medium length, title case = H2
        else if (plainText.length < 50) {
          level = 2
        }
        // Longer text = H3
        else {
          level = 3
        }

        return {
          ...block,
          type: 'header',
          data: {
            text: plainText,
            level: level,
          },
        }
      }
    }

    return block
  })
}

export const handleExportPDF =
  (
    editor: any,
    documentPublicId?: string,
    filename: string = 'document',
    onSubscribe?: (errorCode?: BillingModalCopyKey) => void,
    onExportSuccess?: () => void
  ) =>
  async () => {
    if (!documentPublicId) return

    // fire download_attempted event
    eventsService.fireEvent({
      name: EventName.DOWNLOAD_ATTEMPTED,
      category: EventCategory.CORE_BEHAVIOR,
      doc_public_id: documentPublicId,
      source: 'TOOLBAR_DOWNLOAD_PDF',
      meta: {},
    })

    // Check export eligibility
    const checkPassed = await docsService
      .checkExportEligibility(documentPublicId)
      .then(() => true)
      .catch(error => {
        const errorCode = error?.response?.data?.errorCode
        if (errorCode) onSubscribe?.(errorCode)
        else toast.error('Unable to check export eligibility. Please try again.')

        return false
      })

    if (!checkPassed) return

    try {
      const htmlContent = await getHtmlFromEditor(editor)

      const container = document.createElement('div')
      container.innerHTML = htmlContent

      // Remove line-through styling from suggestion blocks for PDF
      const suggestionOriginalElements = container.querySelectorAll('.suggestion-original *')
      suggestionOriginalElements.forEach(element => {
        ;(element as HTMLElement).style.textDecoration = 'none'
      })

      html2pdf()
        .from(container)
        .set({
          margin: 10,
          filename: `${filename}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .save()

      // fire download_completed event
      eventsService.fireEvent({
        name: EventName.DOWNLOAD_COMPLETED,
        category: EventCategory.CORE_BEHAVIOR,
        doc_public_id: documentPublicId,
        source: 'TOOLBAR_DOWNLOAD_PDF',
        meta: {},
      })

      // fire doc_completed event
      eventsService.fireEvent({
        name: EventName.DOC_COMPLETED,
        category: EventCategory.CORE_BEHAVIOR,
        doc_public_id: documentPublicId,
        source: undefined,
        meta: {},
      })

      // Call success callback to update metadata
      onExportSuccess?.()
    } catch (e) {}
  }

function htmlToWordContent(htmlString: string): Paragraph[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, 'text/html')
  const paragraphs: Paragraph[] = []

  // Helper function to extract text and formatting from a node
  const extractTextRuns = (node: Node): TextRun[] => {
    const textRuns: TextRun[] = []

    const processNode = (currentNode: Node): void => {
      if (currentNode.nodeType === Node.TEXT_NODE) {
        const text = currentNode.textContent?.trim()
        if (text) {
          textRuns.push(new TextRun({ text }))
        }
      } else if (currentNode.nodeType === Node.ELEMENT_NODE) {
        const element = currentNode as Element
        const tagName = element.tagName.toLowerCase()
        const text = element.textContent?.trim()

        if (!text) return

        // Handle formatting elements
        if (tagName === 'strong' || tagName === 'b') {
          textRuns.push(new TextRun({ text, bold: true }))
        } else if (tagName === 'em' || tagName === 'i') {
          textRuns.push(new TextRun({ text, italics: true }))
        } else if (tagName === 'u') {
          textRuns.push(new TextRun({ text, underline: {} }))
        } else if (tagName === 'mark') {
          textRuns.push(new TextRun({ text, highlight: 'yellow' }))
        } else if (tagName === 'a') {
          const href = element.getAttribute('href') || '#'
          textRuns.push(new TextRun({ text, color: '0000FF' }))
        } else {
          // For other elements, process children recursively
          Array.from(currentNode.childNodes).forEach(processNode)
        }
      }
    }

    Array.from(node.childNodes).forEach(processNode)
    return textRuns
  }

  // Process each element in the HTML
  const processElement = (element: Element): void => {
    const tagName = element.tagName.toLowerCase()
    const textContent = element.textContent?.trim()

    if (!textContent) return

    if (tagName.match(/^h[1-6]$/)) {
      // Handle headings
      const level = parseInt(tagName.charAt(1)) as 1 | 2 | 3 | 4 | 5 | 6
      const headingLevel = [
        HeadingLevel.HEADING_1,
        HeadingLevel.HEADING_2,
        HeadingLevel.HEADING_3,
        HeadingLevel.HEADING_4,
        HeadingLevel.HEADING_5,
        HeadingLevel.HEADING_6,
      ][level - 1]

      paragraphs.push(
        new Paragraph({
          heading: headingLevel,
          children: extractTextRuns(element),
          spacing: { after: 200 },
        })
      )
    } else if (tagName === 'p') {
      // Handle paragraphs
      const textRuns = extractTextRuns(element)
      if (textRuns.length > 0) {
        paragraphs.push(
          new Paragraph({
            children: textRuns,
            spacing: { after: 120 },
          })
        )
      }
    } else if (tagName === 'ul' || tagName === 'ol') {
      // Handle lists
      const listItems = element.querySelectorAll('li')
      listItems.forEach((li, index) => {
        const listTextRuns = extractTextRuns(li)
        if (listTextRuns.length > 0) {
          paragraphs.push(
            new Paragraph({
              children: listTextRuns,
              bullet: tagName === 'ul' ? { level: 0 } : undefined,
              numbering: tagName === 'ol' ? { reference: 'default-numbering', level: 0 } : undefined,
              spacing: { after: 60 },
            })
          )
        }
      })
    } else if (tagName === 'blockquote') {
      // Handle blockquotes
      const textRuns = extractTextRuns(element)
      if (textRuns.length > 0) {
        paragraphs.push(
          new Paragraph({
            children: textRuns,
            indent: { left: 720 }, // 0.5 inch indent
            spacing: { after: 120 },
          })
        )
      }
    } else {
      // For other block elements, treat as paragraphs
      const textRuns = extractTextRuns(element)
      if (textRuns.length > 0) {
        paragraphs.push(
          new Paragraph({
            children: textRuns,
            spacing: { after: 120 },
          })
        )
      }
    }
  }

  // Process all block-level elements
  const blockElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6, p, div, ul, ol, blockquote')
  blockElements.forEach(processElement)

  return paragraphs
}

/**
 * Exports the current editor content as a Word document
 */
export const handleExportWord =
  (
    editor: any,
    documentPublicId?: string,
    filename: string = 'document',
    onSubscribe?: (errorCode?: BillingModalCopyKey) => void,
    onExportSuccess?: () => void
  ) =>
  async () => {
    if (!documentPublicId) return

    // Fire download_attempted event
    eventsService.fireEvent({
      name: EventName.DOWNLOAD_ATTEMPTED,
      category: EventCategory.CORE_BEHAVIOR,
      doc_public_id: documentPublicId,
      source: 'TOOLBAR_DOWNLOAD_DOCX',
      meta: {},
    })

    // Check export eligibility
    const checkPassed = await docsService
      .checkExportEligibility(documentPublicId)
      .then(() => true)
      .catch(error => {
        const errorCode = error?.response?.data?.errorCode
        if (errorCode) onSubscribe?.(errorCode)
        else toast.error('Unable to check export eligibility. Please try again.')

        return false
      })

    if (!checkPassed) return

    try {
      // Get HTML content from editor
      const htmlContent = await getHtmlFromEditor(editor)
      const cleanedHtml = cleanHtmlContent(htmlContent)

      // Convert HTML to Word paragraphs
      const paragraphs = htmlToWordContent(cleanedHtml)

      // Create Word document
      const doc = new Document({
        styles: {
          default: {
            document: {
              run: {
                font: 'Times New Roman',
                size: 24, // 12pt in Word
              },
            },
          },
          paragraphStyles: [
            {
              id: 'Normal',
              name: 'Normal',
              run: {
                font: 'Times New Roman',
                size: 24,
              },
              paragraph: {
                alignment: AlignmentType.LEFT,
                spacing: { line: 276 }, // 1.15 line spacing
              },
            },
          ],
        },
        numbering: {
          config: [
            {
              reference: 'default-numbering',
              levels: [
                {
                  level: 0,
                  format: 'decimal',
                  text: '%1.',
                  alignment: AlignmentType.START,
                },
              ],
            },
          ],
        },
        sections: [
          {
            children:
              paragraphs.length > 0
                ? paragraphs
                : [
                    new Paragraph({
                      children: [new TextRun({ text: 'No content to export.' })],
                    }),
                  ],
          },
        ],
      })

      // Generate and download the Word document
      const blob = await Packer.toBlob(doc)
      saveAs(blob, `${filename}.docx`)

      // Fire download_completed event
      eventsService.fireEvent({
        name: EventName.DOWNLOAD_COMPLETED,
        category: EventCategory.CORE_BEHAVIOR,
        doc_public_id: documentPublicId,
        source: 'TOOLBAR_DOWNLOAD_DOCX',
        meta: {},
      })

      // Fire doc_completed event
      eventsService.fireEvent({
        name: EventName.DOC_COMPLETED,
        category: EventCategory.CORE_BEHAVIOR,
        doc_public_id: documentPublicId,
        source: undefined,
        meta: {},
      })

      // Call success callback to update metadata
      onExportSuccess?.()
    } catch (error) {
      console.error('Error exporting to Word:', error)
    }
  }

// auto select the text in the editor given a block id
export function selectTextInBlock(editor: LexicalEditor, blockId: string) {
  editor.update(() => {
    // Get the root and search through all children
    const root = $getRoot()
    const children = root.getChildren()

    // Find the block with matching __blockId using recursive search
    const targetBlock = findNodeRecursively(
      children,
      node => (node as LexicalNode & { __blockId?: string }).__blockId === blockId
    )

    if (targetBlock && $isElementNode(targetBlock)) {
      // Create a range selection for the entire block
      const selection = $createRangeSelection()
      selection.anchor.set(targetBlock.getKey(), 0, 'element')
      selection.focus.set(targetBlock.getKey(), targetBlock.getChildren().length, 'element')
      $setSelection(selection)
    }
  })
}

// Helper function to recursively search through nested children
export function findNodeRecursively(
  nodes: LexicalNode[],
  predicate: (node: LexicalNode) => boolean,
  currentLevel: number = 0,
  maxLevel: number = 3
): LexicalNode | null {
  for (const node of nodes) {
    // Check if current node matches the predicate
    if (predicate(node)) {
      return node
    }

    // If we haven't reached max level and node has children, search recursively
    if (currentLevel < maxLevel && $isElementNode(node)) {
      const childNodes = node.getChildren()
      const foundNode = findNodeRecursively(childNodes, predicate, currentLevel + 1, maxLevel)
      if (foundNode) {
        return foundNode
      }
    }
  }
  return null
}

export function updateSuggestionContent(editor: LexicalEditor, suggestionId: string, newContent: string) {
  editor.update(() => {
    // Find the suggestion node by ID
    const root = $getRoot()
    const children = root.getChildren()

    // Find the suggestion node using recursive search
    const suggestionNode = findNodeRecursively(
      children,
      node => (node as LexicalNode & { __suggestionData?: SuggestionData }).__suggestionData?.id === suggestionId
    )

    if (suggestionNode && $isSuggestionNode(suggestionNode)) {
      // Update the suggestion data
      const currentData = suggestionNode.getSuggestionData()
      suggestionNode.setSuggestionData({
        ...currentData,
        suggestedHtml: newContent,
      })
    }
  })
}

export async function handleApplySingleImprovement(args: {
  editor: LexicalEditor
  risk: EngineAiOutput['riskCards'][number]
  documentVersionNumber: number
  documentPublicId: string
  testMode: boolean
  documentSummary: string
  onStreamStart: () => void
  onStreamEnd: (finalText: string) => void
  onStreamError: (error: string | { error?: string; errorCode?: BillingModalCopyKey }) => void
  onTextChunk: (text: string) => void
}) {
  const {
    editor,
    risk,
    onStreamStart,
    onStreamEnd,
    onStreamError,
    onTextChunk,
    documentPublicId,
    documentVersionNumber,
    testMode,
    documentSummary,
  } = args

  let currentSuggestionId: string | null = null

  const payload: StreamingTextActionDto = {
    action: 'apply_improvement',
    documentVersionNumber,
    documentPublicId,
    documentSummary,
    riskCard: risk,
    testMode,
  }

  await aiService.streamingTextAction(
    payload,
    (
      event: string,
      data: { fullText?: string; finalText?: string; error?: string; errorCode?: BillingModalCopyKey }
    ) => {
      if (event === 'error') {
        onStreamError?.(data)
      } else if (event === 'stream_start') {
        onStreamStart?.()

        const suggestionId = `suggestion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        editor.dispatchCommand(CREATE_SUGGESTION_COMMAND, {
          suggestedHtml: '...',
          suggestionId: suggestionId,
        })

        currentSuggestionId = suggestionId
      } else if (event === 'text_chunk') {
        onTextChunk?.(data.fullText || '')
        // Update the suggestion content using Lexical node system
        if (currentSuggestionId && data.fullText) {
          updateSuggestionContent(editor, currentSuggestionId, data.fullText)
        }
      } else if (event === 'stream_end') {
        onStreamEnd?.(data.finalText || '')

        if (data.finalText) {
          // Final update to the suggestion content
          if (currentSuggestionId) {
            updateSuggestionContent(editor, currentSuggestionId, data.finalText)
          }
        }
      }
    },
    (error: Record<string, any>) => {
      onStreamError?.(error)
    }
  )
}
